import React, { FC, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { LuInfo } from "react-icons/lu";

import { IUser } from "../../types/types";
import { ClientTeamPerson } from "./clientTeam.types";
import { CLIENT_TEAM_INFO, personInitials } from "./clientTeam.utils";

/** The ⓘ button (owner asked for it): a short explanation of the two roles. */
export const ClientTeamInfoButton: FC<{ className?: string }> = ({
  className = "",
}) => {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  useLayoutEffect(() => {
    if (!open || !buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const width = 320;
    const left = Math.max(
      12,
      Math.min(rect.left - 12, window.innerWidth - width - 12),
    );
    setPosition({ top: rect.bottom + 8, left });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        !panelRef.current?.contains(target) &&
        !buttonRef.current?.contains(target)
      )
        setOpen(false);
    };
    const onScroll = () => setOpen(false);
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onDown);
    window.addEventListener("scroll", onScroll, true);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onDown);
      window.removeEventListener("scroll", onScroll, true);
    };
  }, [open]);

  return (
    <>
      <button
        ref={buttonRef}
        type={"button"}
        aria-label={"What are the client owner and client manager?"}
        aria-expanded={open}
        title={"What are the client owner and client manager?"}
        onClick={() => setOpen((v) => !v)}
        className={`inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[#9097A8] hover:bg-[#F4F2FF] hover:text-[#6B4EFF] ${className}`}>
        <LuInfo size={15} strokeWidth={2.2} />
      </button>
      {open &&
        createPortal(
          <div
            ref={panelRef}
            role={"dialog"}
            aria-label={"Client team"}
            className={
              "fixed z-[1100] max-h-[70vh] w-[320px] overflow-y-auto rounded-xl border border-[#DCE2EA] bg-white p-4 font-Manrope text-[13px] leading-[1.5] text-[#3A4256] shadow-[0_12px_32px_rgba(31,36,51,0.18)]"
            }
            style={{ top: position.top, left: position.left }}>
            <p className={"m-0"}>
              <b className={"text-[#1F2433]"}>Client manager:</b>{" "}
              {CLIENT_TEAM_INFO.manager.replace(/^Client manager: /, "")}
            </p>
            <p className={"m-0 mt-2"}>
              <b className={"text-[#1F2433]"}>Client owner:</b>{" "}
              {CLIENT_TEAM_INFO.owner.replace(/^Client owner: /, "")}
            </p>
            <p className={"m-0 mt-2"}>{CLIENT_TEAM_INFO.both}</p>
          </div>,
          document.body,
        )}
    </>
  );
};

export const PersonAvatar: FC<{
  name?: string | null;
  color?: string | null;
  size?: number;
}> = ({ name, color, size = 34 }) => (
  <span
    aria-hidden
    className={
      "inline-flex shrink-0 items-center justify-center rounded-full font-extrabold text-white"
    }
    style={{
      width: size,
      height: size,
      fontSize: Math.round(size * 0.36),
      background: color || "#6B4EFF",
    }}>
    {personInitials(name)}
  </span>
);

/** One row of the Client team card: avatar, label in capitals, name (or "Not set"). */
export const ClientTeamPersonRow: FC<{
  label: string;
  person?: ClientTeamPerson | null;
}> = ({ label, person }) => (
  <div className={"flex min-w-0 items-center gap-3"}>
    {person ? (
      <PersonAvatar name={person.name} color={person.color} />
    ) : (
      <span
        aria-hidden
        className={
          "inline-flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-full border-[1.5px] border-dashed border-[#DCE2EA] text-[#9097A8]"
        }>
        ?
      </span>
    )}
    <div className={"flex min-w-0 flex-col"}>
      <span
        className={
          "text-[10.5px] font-bold uppercase tracking-[0.06em] text-[#9097A8]"
        }>
        {label}
      </span>
      <span
        className={`truncate text-[13.5px] font-semibold ${
          person ? "text-[#1F2433]" : "text-[#9097A8]"
        }`}>
        {person ? person.name : "Not set"}
        {person?.archived && (
          <span className={"ml-1.5 text-[12px] font-medium text-[#B54708]"}>
            (archived)
          </span>
        )}
      </span>
    </div>
  </div>
);

export const userName = (user: IUser) => {
  const name = `${user.userProfile?.firstName || ""} ${
    user.userProfile?.lastName || ""
  }`.trim();
  return name || user.userProfile?.email || "Unknown person";
};

/** Team members sorted by name (for the pickers). */
export const sortedTeam = (users?: IUser[]) =>
  [...(users ?? [])].sort((a, b) => userName(a).localeCompare(userName(b)));

export const pickerClass =
  "h-10 w-full rounded-[9px] border-[1.5px] border-[#DCE2EA] bg-white px-3 text-[13.5px] text-[#1F2433] outline-none focus:border-[#6B4EFF]";

/** A person picker: "Not set" + the firm's team (+ the current person if they're no longer in it). */
export const PersonPicker: FC<{
  id: string;
  label: string;
  hint: string;
  value: string;
  onChange: (value: string) => void;
  users: IUser[];
  current?: ClientTeamPerson | null;
  emptyLabel?: string;
}> = ({ id, label, hint, value, onChange, users, current, emptyLabel }) => {
  const missing =
    current &&
    !users.some((u) => u.id.toLowerCase() === current.id.toLowerCase());
  return (
    <div className={"flex flex-col gap-1.5"}>
      <label htmlFor={id} className={"text-[13px] font-bold text-[#1F2433]"}>
        {label}
      </label>
      <select
        id={id}
        className={pickerClass}
        value={value}
        onChange={(e) => onChange(e.target.value)}>
        <option value={""}>{emptyLabel ?? "Not set"}</option>
        {missing && current && (
          <option value={current.id}>{current.name} (archived)</option>
        )}
        {users.map((user) => (
          <option key={user.id} value={user.id}>
            {userName(user)}
          </option>
        ))}
      </select>
      <span className={"text-[12px] text-[#6B7185]"}>{hint}</span>
    </div>
  );
};
